import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import toast from "react-hot-toast";
import {
  useCreateBindingTokenMutation,
  useRevokeDeviceMutation,
  useGetDevicesQuery,
} from "../store/trader-api";
import { useDeviceBindingPolling } from "../hooks/useDeviceStatus";

// Стили модального окна
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: var(--bg-modal-overlay);
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-lg);
  max-width: 420px;
  width: 100%;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const IconButton = styled.button<{ variant?: 'default' | 'close' }>`
  width: 32px;
  height: 32px;
  border-radius: 0.375rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.variant === 'close' ? 'transparent' : 'var(--bg-secondary)'};
  color: ${props => props.variant === 'close' ? 'var(--text-secondary)' : 'var(--text-secondary)'};

  &:hover {
    background: ${props => props.variant === 'close' ? 'var(--alert-error-bg)' : 'var(--border-color)'};
    color: ${props => props.variant === 'close' ? 'var(--accent-error)' : 'var(--text-primary)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const QRContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
`;

const QRWrapper = styled.div`
  padding: 1rem;
  background: var(--bg-card);
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  display: inline-block;
`;

const StatusMessage = styled.div<{
  type: "info" | "success" | "warning" | "error";
}>`
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  font-size: 0.875rem;

  ${(props) => {
    switch (props.type) {
      case "success":
        return `
          background: var(--alert-success-bg);
          color: var(--alert-success-text);
          border: 1px solid var(--alert-success-border);
        `;
      case "warning":
        return `
          background: var(--alert-warning-bg);
          color: var(--alert-warning-text);
          border: 1px solid var(--alert-warning-border);
        `;
      case "error":
        return `
          background: var(--alert-error-bg);
          color: var(--alert-error-text);
          border: 1px solid var(--alert-error-border);
        `;
      default:
        return `
          background: var(--alert-info-bg);
          color: var(--alert-info-text);
          border: 1px solid var(--alert-info-border);
        `;
    }
  }}
`;

const CountdownTimer = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-align: center;
  margin: 0.5rem 0;
`;

const DeviceInfo = styled.div`
  background: var(--bg-primary);
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
`;

const DeviceInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem 0;
  font-size: 0.875rem;
`;

const DeviceInfoLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
`;

const DeviceInfoValue = styled.span`
  color: var(--text-primary);
`;

const TokenDisplay = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;
  font-family: monospace;
  font-size: 0.875rem;
  word-break: break-all;
  text-align: center;
  color: var(--text-primary);
`;

const TokenLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CopyButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--accent-primary);
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--accent-primary-hover);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }

  &:active {
    background: var(--accent-primary-hover);
    transform: translateY(0);
  }
`;

const Button = styled.button<{
  variant?: "primary" | "secondary" | "danger";
  disabled?: boolean;
}>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) => {
    if (props.disabled) {
      return `
        background: var(--bg-secondary);
        color: var(--text-muted);
        cursor: not-allowed;
      `;
    }

    switch (props.variant) {
      case "primary":
        return `
          background: var(--accent-primary);
          color: #ffffff;
          &:hover {
            background: var(--accent-primary-hover);
          }
        `;
      case "danger":
        return `
          background: var(--accent-error);
          color: #ffffff;
          &:hover {
            background: var(--alert-error-text);
          }
        `;
      default:
        return `
          background: var(--bg-secondary);
          color: var(--text-secondary);
          &:hover {
            background: var(--border-color);
          }
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--accent-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export interface QRBindingModalProps {
  isOpen: boolean;
  onClose: () => void;
  holderId: string;
  holderName: string;
  deviceId?: string; // Для режима перепривязки
  deviceInfo?: {
    platform: string;
    model: string;
    lastSeenAt?: Date;
    isActive?: boolean;
    health?: {
      isHealthy?: boolean;
      permissionsOk?: boolean;
      notificationsOk?: boolean;
      batteryOptimizationDisabled?: boolean;
      backgroundServiceRunning?: boolean;
      networkOk?: boolean;
      airplaneMode?: boolean;
      lastReportAt?: Date;
    };
  };
  onDeviceStatusChanged?: () => void; // Callback для обновления данных в родительском компоненте
}

const QRBindingModal: React.FC<QRBindingModalProps> = ({
  isOpen,
  onClose,
  holderId,
  holderName,
  deviceId,
  deviceInfo,
  onDeviceStatusChanged,
}) => {
  const [qrData, setQrData] = useState<any>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [step, setStep] = useState<
    "confirm" | "device-info" | "qr" | "polling" | "success"
  >(
    deviceId
      ? deviceInfo?.isActive === false
        ? "device-info"
        : "confirm"
      : "qr"
  );
  const [error, setError] = useState<string | null>(null);

  const [createBindingToken, { isLoading: isCreatingToken }] =
    useCreateBindingTokenMutation();
  const [revokeDevice, { isLoading: isRevoking }] = useRevokeDeviceMutation();

  // Хук для принудительного обновления данных устройств
  const { refetch: refetchDevices } = useGetDevicesQuery(
    { holderId },
    { skip: !holderId }
  );

  // Поллинг для отслеживания привязки
  const bindingStatus = useDeviceBindingPolling(
    holderId,
    isPolling,
    () => {
      setStep("success");
      setIsPolling(false);
      toast.success("Устройство успешно привязано!");

      // Уведомляем родительский компонент об изменениях
      if (onDeviceStatusChanged) {
        onDeviceStatusChanged();
      }

      // Закрываем модалку через 3 секунды
      setTimeout(() => {
        onClose();
      }, 3000);
    },
    () => {
      setIsPolling(false);
      setError("Время ожидания истекло. Попробуйте снова.");
    },
    180000 // Увеличиваем timeout до 3 минут
  );

  // Сброс состояния при открытии модалки
  useEffect(() => {
    if (isOpen) {
      // Определяем начальный шаг в зависимости от состояния устройства
      if (deviceId) {
        if (deviceInfo?.isActive === false) {
          setStep("device-info"); // Устройство деактивировано - показываем информацию
        } else {
          setStep("confirm"); // Устройство активно - показываем подтверждение перепривязки
        }
      } else {
        setStep("qr"); // Устройство не привязано - показываем QR
        generateQR();
      }
      setError(null);
      setIsPolling(false);
    } else {
      setQrData(null);
      setIsPolling(false);
      setError(null);
    }
  }, [isOpen, deviceId, deviceInfo?.isActive]); // generateQR не добавляем в зависимости, т.к. это может вызвать лишние вызовы

  const generateQR = async () => {
    try {
      setError(null);
      const response = await createBindingToken({ holderId }).unwrap();
      setQrData(response);
      setStep("qr");
      // Автоматически запускаем поллинг при генерации QR
      setIsPolling(true);
    } catch (err: any) {
      console.error("Error generating QR:", err);
      setError(
        err?.data?.message || "Ошибка генерации QR-кода. Попробуйте снова."
      );
    }
  };

  // Helper function to truncate token like crypto address
  const truncateToken = (token: string): string => {
    if (token.length <= 20) return token;
    return `${token.slice(0, 12)}...${token.slice(-8)}`;
  };

  const handleStartPolling = () => {
    setStep("polling");
    setIsPolling(true);
    setError(null);
  };

  const handleRevokeAndRebind = async () => {
    if (!deviceId) return;

    try {
      setError(null);
      await revokeDevice({
        deviceId,
        reason: "Перепривязка устройства пользователем",
      }).unwrap();

      toast.success("Устройство отвязано");

      // Принудительно обновляем данные устройств
      await refetchDevices();

      // Уведомляем родительский компонент об изменениях
      if (onDeviceStatusChanged) {
        onDeviceStatusChanged();
      }

      // Генерируем новый QR
      await generateQR();
    } catch (err: any) {
      console.error("Error revoking device:", err);
      setError(
        err?.data?.message || "Ошибка отвязки устройства. Попробуйте снова."
      );
    }
  };

  const formatTimeRemaining = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatLastSeen = (lastSeenAt?: Date): string => {
    if (!lastSeenAt) return "Неизвестно";

    const now = Date.now();
    const lastSeen = new Date(lastSeenAt).getTime();
    const diffMs = now - lastSeen;

    if (diffMs < 60000) return "Только что";
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)} мин назад`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)} ч назад`;
    return `${Math.floor(diffMs / 86400000)} дн назад`;
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {step === "device-info"
              ? "Устройство деактивировано"
              : deviceId
                ? "Перепривязка устройства"
                : "Привязка устройства"}
          </ModalTitle>
          <HeaderActions>
            {step === "qr" && qrData && (
              <IconButton
                title="Сгенерировать заново"
                onClick={generateQR}
                disabled={isCreatingToken}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 2v6h-6" />
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M3 22v-6h6" />
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
              </IconButton>
            )}
            <IconButton variant="close" onClick={onClose} title="Закрыть">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </IconButton>
          </HeaderActions>
        </ModalHeader>

        <ModalBody>
          {/* Шаг информации о деактивированном устройстве */}
          {step === "device-info" && deviceId && deviceInfo && (
            <>
              <StatusMessage type="error">
                <strong>Устройство деактивировано!</strong> Последняя попытка
                healthcheck не удалась. Проверьте приложение на устройстве.
              </StatusMessage>

              <DeviceInfo>
                <h4 style={{ margin: "0 0 1rem 0", color: "var(--text-primary)" }}>
                  Информация об устройстве
                </h4>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Платформа:</DeviceInfoLabel>
                  <DeviceInfoValue>{deviceInfo.platform}</DeviceInfoValue>
                </DeviceInfoRow>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Модель:</DeviceInfoLabel>
                  <DeviceInfoValue>{deviceInfo.model}</DeviceInfoValue>
                </DeviceInfoRow>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Последняя активность:</DeviceInfoLabel>
                  <DeviceInfoValue>
                    {formatLastSeen(deviceInfo.lastSeenAt)}
                  </DeviceInfoValue>
                </DeviceInfoRow>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Статус:</DeviceInfoLabel>
                  <DeviceInfoValue
                    style={{
                      color: "var(--accent-error)",
                      fontWeight: 600,
                    }}
                  >
                    Деактивировано
                  </DeviceInfoValue>
                </DeviceInfoRow>
                {deviceInfo.health?.lastReportAt && (
                  <DeviceInfoRow>
                    <DeviceInfoLabel>Последний healthcheck:</DeviceInfoLabel>
                    <DeviceInfoValue>
                      {formatLastSeen(deviceInfo.health.lastReportAt)}
                    </DeviceInfoValue>
                  </DeviceInfoRow>
                )}
              </DeviceInfo>

              {/* Детализация проблем */}
              {deviceInfo.health && (
                <div
                  style={{
                    background: "var(--alert-error-bg)",
                    border: "1px solid var(--alert-error-border)",
                    borderRadius: "0.5rem",
                    padding: "1rem",
                    marginTop: "1rem",
                  }}
                >
                  <h5
                    style={{
                      margin: "0 0 0.75rem 0",
                      color: "var(--accent-error)",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    Причины деактивации:
                  </h5>
                  <div style={{ fontSize: "0.8rem", color: "var(--alert-error-text)" }}>
                    {!deviceInfo.health.permissionsOk && (
                      <div>• Нет разрешений на SMS/уведомления</div>
                    )}
                    {!deviceInfo.health.notificationsOk && (
                      <div>• Проблемы с доступом к уведомлениям</div>
                    )}
                    {!deviceInfo.health.batteryOptimizationDisabled && (
                      <div>
                        • Включена оптимизация батареи (отключите в настройках)
                      </div>
                    )}
                    {!deviceInfo.health.backgroundServiceRunning && (
                      <div>• Фоновый сервис не запущен</div>
                    )}
                    {!deviceInfo.health.networkOk && (
                      <div>• Проблемы с сетевым подключением</div>
                    )}
                    {deviceInfo.health.airplaneMode && (
                      <div>• Включен авиарежим</div>
                    )}
                    {!deviceInfo.health.permissionsOk &&
                      !deviceInfo.health.notificationsOk &&
                      !deviceInfo.health.batteryOptimizationDisabled &&
                      !deviceInfo.health.backgroundServiceRunning &&
                      !deviceInfo.health.networkOk &&
                      !deviceInfo.health.airplaneMode && (
                        <div>
                          • Устройство не отправляло healthcheck (возможно,
                          приложение закрыто или удалено)
                        </div>
                      )}
                  </div>
                </div>
              )}

              <div
                style={{
                  background: "var(--alert-info-bg)",
                  border: "1px solid var(--alert-info-border)",
                  borderRadius: "0.5rem",
                  padding: "1rem",
                  marginTop: "1rem",
                  fontSize: "0.875rem",
                  color: "var(--alert-info-text)",
                }}
              >
                <strong>Что делать:</strong>
                <ol
                  style={{ margin: "0.5rem 0 0 1rem", paddingLeft: "0.5rem" }}
                >
                  <li>Откройте приложение VangardApp на устройстве</li>
                  <li>Проверьте и исправьте указанные проблемы</li>
                  <li>
                    Дождитесь автоматической активации (устройство отправит
                    healthcheck)
                  </li>
                  <li>
                    Или перепривяжите устройство заново, если проблема не
                    решается
                  </li>
                </ol>
              </div>

              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.875rem",
                  marginTop: "1rem",
                }}
              >
                Для держателя: <strong>{holderName}</strong>
              </p>
            </>
          )}

          {/* Шаг подтверждения перепривязки */}
          {step === "confirm" && deviceId && deviceInfo && (
            <>
              <StatusMessage type="warning">
                <strong>Внимание!</strong> Это действие отвяжет текущее
                устройство и потребует повторной привязки.
              </StatusMessage>

              <DeviceInfo>
                <h4 style={{ margin: "0 0 1rem 0", color: "var(--text-primary)" }}>
                  Текущее устройство
                </h4>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Платформа:</DeviceInfoLabel>
                  <DeviceInfoValue>{deviceInfo.platform}</DeviceInfoValue>
                </DeviceInfoRow>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Модель:</DeviceInfoLabel>
                  <DeviceInfoValue>{deviceInfo.model}</DeviceInfoValue>
                </DeviceInfoRow>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Последняя активность:</DeviceInfoLabel>
                  <DeviceInfoValue>
                    {formatLastSeen(deviceInfo.lastSeenAt)}
                  </DeviceInfoValue>
                </DeviceInfoRow>
                <DeviceInfoRow>
                  <DeviceInfoLabel>Статус:</DeviceInfoLabel>
                  <DeviceInfoValue
                    style={{
                      color: deviceInfo.isActive ? "var(--accent-success)" : "var(--accent-error)",
                      fontWeight: 600,
                    }}
                  >
                    {deviceInfo.isActive ? "Активно" : "Деактивировано"}
                  </DeviceInfoValue>
                </DeviceInfoRow>
              </DeviceInfo>

              {/* Детализация проблем */}
              {deviceInfo.health &&
                (!deviceInfo.isActive ||
                  !deviceInfo.health.permissionsOk ||
                  !deviceInfo.health.notificationsOk ||
                  !deviceInfo.health.batteryOptimizationDisabled ||
                  !deviceInfo.health.backgroundServiceRunning ||
                  !deviceInfo.health.networkOk ||
                  deviceInfo.health.airplaneMode) && (
                  <div
                    style={{
                      background: "var(--alert-error-bg)",
                      border: "1px solid var(--alert-error-border)",
                      borderRadius: "0.5rem",
                      padding: "1rem",
                      marginTop: "1rem",
                    }}
                  >
                    <h5
                      style={{
                        margin: "0 0 0.75rem 0",
                        color: "var(--accent-error)",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                    >
                      Обнаруженные проблемы:
                    </h5>
                    <div style={{ fontSize: "0.8rem", color: "var(--alert-error-text)" }}>
                      {!deviceInfo.health.permissionsOk && (
                        <div>• Нет разрешений на SMS/уведомления</div>
                      )}
                      {!deviceInfo.health.notificationsOk && (
                        <div>• Проблемы с доступом к уведомлениям</div>
                      )}
                      {!deviceInfo.health.batteryOptimizationDisabled && (
                        <div>• Включена оптимизация батареи</div>
                      )}
                      {!deviceInfo.health.backgroundServiceRunning && (
                        <div>• Фоновый сервис не запущен</div>
                      )}
                      {!deviceInfo.health.networkOk && (
                        <div>• Проблемы с сетевым подключением</div>
                      )}
                      {deviceInfo.health.airplaneMode && (
                        <div>• Включен авиарежим</div>
                      )}
                    </div>
                  </div>
                )}

              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.875rem",
                  marginTop: "1rem",
                }}
              >
                Для держателя: <strong>{holderName}</strong>
              </p>
            </>
          )}

          {/* Шаг отображения QR */}
          {step === "qr" && (
            <>
              <StatusMessage type="info">
                Отсканируйте QR-код приложением VangardApp для привязки
                устройства автоматики к держателю <strong>{holderName}</strong>
              </StatusMessage>

              {qrData ? (
                <QRContainer>
                  <QRWrapper>
                    <QRCodeSVG
                      value={JSON.stringify({
                        bindingToken: qrData.token,
                        holderId: qrData.holderId,
                        exp: qrData.expiresAt,
                        backendBaseUrl: qrData.backendBaseUrl,
                      })}
                      size={180}
                      level="M"
                      includeMargin={false}
                    />
                  </QRWrapper>

                  <CountdownTimer>
                    Токен действует до:{" "}
                    {new Date(qrData.expiresAt).toLocaleTimeString()}
                  </CountdownTimer>

                  {/* Токен и копирование */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem" }}>
                    <TokenLabel>Токен привязки:</TokenLabel>
                    <TokenDisplay title={qrData.token} style={{ cursor: "pointer", padding: "0.5rem", fontSize: "0.8rem" }}>{truncateToken(qrData.token)}</TokenDisplay>
                    <CopyButton
                      onClick={() => {
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(qrData.token);
                          toast.success("Токен скопирован в буфер обмена");
                        } else {
                          // Fallback for non-secure contexts
                          const textArea = document.createElement("textarea");
                          textArea.value = qrData.token;
                          document.body.appendChild(textArea);
                          textArea.select();
                          try {
                            document.execCommand("copy");
                            toast.success("Токен скопирован в буфер обмена");
                          } catch (err) {
                            console.error("Fallback: Oops, unable to copy", err);
                            toast.error("Не удалось скопировать токен");
                          }
                          document.body.removeChild(textArea);
                        }
                      }}
                    >
                      Скопировать токен
                    </CopyButton>
                  </div>

                  {isPolling && (
                    <StatusMessage type="info">
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <LoadingSpinner />
                        Ожидаем привязку устройства...
                      </div>
                    </StatusMessage>
                  )}
                </QRContainer>
              ) : (
                <QRContainer>
                  <LoadingSpinner />
                  <p>Генерация QR-кода...</p>
                </QRContainer>
              )}
            </>
          )}

          {/* Шаг поллинга */}
          {step === "polling" && (
            <>
              <StatusMessage type="info">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <LoadingSpinner />
                  Ожидаем подтверждения с устройства...
                </div>
              </StatusMessage>

              <QRContainer>
                <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>
                  Откройте приложение VangardApp и завершите настройку
                  устройства.
                  <br />
                  Проверяем состояние автоматически.
                </p>

                <CountdownTimer>
                  Осталось времени:{" "}
                  {formatTimeRemaining(bindingStatus.timeRemaining)}
                </CountdownTimer>

                {bindingStatus.isBound && !bindingStatus.isHealthy && (
                  <StatusMessage type="warning">
                    Устройство привязано, но требует настройки разрешений.
                    Проверьте настройки приложения на устройстве.
                  </StatusMessage>
                )}
              </QRContainer>
            </>
          )}

          {/* Шаг успеха */}
          {step === "success" && (
            <>
              <StatusMessage type="success">
                <strong>Успешно!</strong> Устройство привязано и готово к
                работе. Модальное окно закроется автоматически.
              </StatusMessage>

              <QRContainer>
                <div style={{ fontSize: "3rem", color: "var(--accent-success)" }}>✓</div>
                <p
                  style={{
                    textAlign: "center",
                    color: "var(--alert-success-text)",
                    fontWeight: 500,
                  }}
                >
                  Устройство успешно привязано к держателю {holderName}
                </p>
              </QRContainer>
            </>
          )}

          {/* Ошибки */}
          {error && (
            <StatusMessage type="error">
              <strong>Ошибка:</strong> {error}
            </StatusMessage>
          )}
        </ModalBody>

        <ModalFooter>
          {step === "device-info" && (
            <>
              <Button onClick={onClose}>Закрыть</Button>
              <Button
                variant="danger"
                onClick={handleRevokeAndRebind}
                disabled={isRevoking}
              >
                {isRevoking ? (
                  <>
                    <LoadingSpinner />
                    Перепривязываем...
                  </>
                ) : (
                  "Перепривязать устройство"
                )}
              </Button>
            </>
          )}

          {step === "confirm" && (
            <>
              <Button onClick={onClose}>Отмена</Button>
              <Button
                variant="danger"
                onClick={handleRevokeAndRebind}
                disabled={isRevoking}
              >
                {isRevoking ? (
                  <>
                    <LoadingSpinner />
                    Отвязываем...
                  </>
                ) : (
                  "Отвязать и привязать заново"
                )}
              </Button>
            </>
          )}

          {/* Удалено - кнопки перенесены в header как иконки */}

          {step === "polling" && (
            <>
              <Button onClick={() => setStep("qr")}>Назад к QR</Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsPolling(false);
                  setStep("qr");
                }}
              >
                Остановить ожидание
              </Button>
            </>
          )}

          {step === "success" && (
            <Button variant="primary" onClick={onClose}>
              Готово
            </Button>
          )}
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default QRBindingModal;
